import { createContext, useContext, useEffect, useRef, useCallback, ReactNode } from 'react';
import { WebSocketEvent } from '@laxmi-ai/shared';
import type { WebSocketMessage } from '@laxmi-ai/shared';
import { useStreamStore } from '../store/websocket-store.js';
import { useTaskStore } from '../store/task-store.js';

type EventHandler = (payload: unknown) => void;

const BINARY_VIDEO_CHUNK = 0x01;

interface ParsedVideoChunk {
  deviceSerial: string;
  pts: number;
  keyFrame: boolean;
  config: boolean;
  data: Uint8Array;
}

function parseBinaryVideoChunk(buffer: ArrayBuffer): ParsedVideoChunk | null {
  const view = new DataView(buffer);
  let offset = 0;
  const type = view.getUint8(offset++);
  if (type !== BINARY_VIDEO_CHUNK) return null;
  const serialLen = view.getUint16(offset);
  offset += 2;
  const serial = new TextDecoder().decode(new Uint8Array(buffer, offset, serialLen));
  offset += serialLen;
  const pts = Number(view.getBigUint64(offset));
  offset += 8;
  const flags = view.getUint8(offset++);
  const keyFrame = (flags & 0x01) !== 0;
  const config = (flags & 0x02) !== 0;
  const dataLen = view.getUint32(offset);
  offset += 4;
  const data = new Uint8Array(buffer, offset, dataLen);
  return { deviceSerial: serial, pts, keyFrame, config, data };
}

export interface WebSocketContextValue {
  connected: boolean;
  /** Returns true if the message was written to an open socket. When false the
   * connection is down or opening — the caller should surface a "not
   * connected" state rather than silently dropping the interaction. */
  send: (event: WebSocketEvent, payload: unknown) => boolean;
  subscribe: (event: string, handler: EventHandler) => void;
  unsubscribe: (event: string, handler: EventHandler) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export { WebSocketContext };

export function useWebSocketContext(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (ctx === null) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return ctx;
}

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Map<string, Set<EventHandler>>>(new Map());
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isDestroyedRef = useRef(false);
  const setConnected = useStreamStore((state) => state.setConnected);
  const setActiveStream = useStreamStore((state) => state.setActiveStream);
  const setTaskProgress = useTaskStore((state) => state.setTaskProgress);

  const send = useCallback((event: WebSocketEvent, payload: unknown): boolean => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        event,
        payload,
        timestamp: new Date().toISOString(),
      }));
      return true;
    }
    return false;
  }, []);

  const subscribe = useCallback((event: string, handler: EventHandler): void => {
    const existing = handlersRef.current.get(event) ?? new Set();
    existing.add(handler);
    handlersRef.current.set(event, existing);
  }, []);

  const unsubscribe = useCallback((event: string, handler: EventHandler): void => {
    const existing = handlersRef.current.get(event);
    if (existing !== undefined) {
      existing.delete(handler);
      if (existing.size === 0) {
        handlersRef.current.delete(event);
      }
    }
  }, []);

  const connect = useCallback(() => {
    if (isDestroyedRef.current) return;

    const token = localStorage.getItem('auth_token') ?? '';
    const isSecure = window.location.protocol === 'https:';
    const wsUrl = `${isSecure ? 'wss' : 'ws'}://${window.location.host}/ws?token=${encodeURIComponent(token)}`;

    try {
      // Never log the URL: it carries the auth token. Log the host only.
      console.log('[WebSocket] Connecting to', window.location.host);
      const ws = new WebSocket(wsUrl);
      // Binary video frames must arrive as ArrayBuffer, not Blob, or the
      // onmessage handler's `instanceof ArrayBuffer` check drops every chunk.
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isDestroyedRef.current) {
          console.log('[WebSocket] Connected');
          reconnectAttemptsRef.current = 0;
          setConnected(true);
        }
      };

      ws.onmessage = (event: MessageEvent) => {
        if (isDestroyedRef.current) return;

        if (event.data instanceof ArrayBuffer) {
          const parsed = parseBinaryVideoChunk(event.data);
          if (parsed !== null) {
            const handlers = handlersRef.current.get(WebSocketEvent.STREAM_VIDEO_CHUNK);
            if (handlers !== undefined) {
              for (const handler of handlers) {
                try {
                  handler(parsed);
                } catch {
                  // handler error ignored
                }
              }
            }
          }
          return;
        }

        try {
          const message = JSON.parse(event.data) as WebSocketMessage;

          const handlers = handlersRef.current.get(message.event);
          if (handlers !== undefined) {
            for (const handler of handlers) {
              try {
                handler(message.payload);
              } catch {
                // handler error ignored
              }
            }
          }

          switch (message.event) {
            case 'stream:frame': {
              const payload = message.payload as { deviceSerial: string; data: string };
              setActiveStream(payload.deviceSerial, `data:image/jpeg;base64,${payload.data}`);
              break;
            }
            case 'task:progress': {
              const payload = message.payload as {
                taskId: string;
                status: string;
                progress: number;
                message: string;
                deviceSerial?: string;
              };
              setTaskProgress({
                taskId: payload.taskId,
                status: payload.status,
                progress: payload.progress,
                message: payload.message,
                deviceSerial: payload.deviceSerial ?? 'unknown',
              });
              break;
            }
            default:
              break;
          }
        } catch {
          // parse error ignored
        }
      };

      ws.onclose = (event: CloseEvent) => {
        wsRef.current = null;
        if (!isDestroyedRef.current) {
          console.log('[WebSocket] Disconnected', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });
          setConnected(false);
          // Exponential backoff (5s → 30s cap) so a hard outage does not
          // hammer the server with a reconnect every 5 seconds.
          const delay = Math.min(
            5000 * 2 ** reconnectAttemptsRef.current,
            30000,
          );
          reconnectAttemptsRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = (event: Event) => {
        console.error('[WebSocket] Error', event);
        // Do NOT manually close here - let the browser handle it
        // Closing manually causes double-close and prevents onclose from firing properly
      };
    } catch (error) {
      console.error('[WebSocket] Connection failed', error);
      if (!isDestroyedRef.current) {
        const delay = Math.min(
          5000 * 2 ** reconnectAttemptsRef.current,
          30000,
        );
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      }
    }
  }, [setConnected, setActiveStream, setTaskProgress]);

  useEffect(() => {
    connect();

    return () => {
      isDestroyedRef.current = true;
      if (reconnectTimeoutRef.current != null) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current !== null) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const value: WebSocketContextValue = {
    connected: useStreamStore((state) => state.connected),
    send,
    subscribe,
    unsubscribe,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}